/**
 * LoadExpandedRow Component
 * Reference-matched expanded pane with resize handles between columns.
 *
 * Two layouts depending on whether an inline email column should render:
 *   • 4-col (load-hub):    Equipment | Trip/Map | Broker | Email
 *   • 3-col (agentic-hub): Equipment | Trip/Map | Broker
 *
 * Agentic-hub routes broker email clicks to the V4 right rail and passes
 * `showInlineEmailChat={false}` so col 4 is omitted. Load-hub has no rail
 * and renders the email chat inline as before.
 *
 * Layout:
 *   - No gap/padding between columns — separation via ResizeHandle (8px)
 *   - Percentage-based widths stored in `colWidths` state
 *   - Mousedown on handle captures startX + startWidths, then clamps widths on drag
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { CanonicalLoad } from '@numeo/types-core'
import { cn } from 'utils/cn'

import { useConvoyOfferDetail } from '../hooks/useConvoyOfferDetail'
import { useLoadDetailFetch } from '../hooks/useLoadDetailFetch'
import { ConvoyDetailSection } from './expanded-row/ConvoyDetailSection'
import { EquipmentDetailsSection } from './expanded-row/EquipmentDetailsSection'
import { InformationSection } from './expanded-row/InformationSection'
import { TripScheduleSection } from './expanded-row/TripScheduleSection'
import { OfferBookEmailCompose } from './expanded-row/OfferBookEmailCompose'
import { PrivateLoadEmailChat } from './expanded-row/PrivateLoadEmailChat'
import { ResizeHandle } from './expanded-row/ResizeHandle'
import { LocationData } from '../types/filters'
import { trackLoadInteraction } from '../utils/sourceAnalytics'
import {
    trackCallHandoffResolved,
    trackEmailComposerOpened,
    trackDetailToolOpened,
    trackLoadContactClicked,
    trackLoadContactCopied,
    trackLoadDetailsOpened,
    trackLoadDetailsResolved,
    toDetailedEmailType,
} from '../utils/loadAttributionAnalytics'
import { getCurrentEmailConfig } from '../hooks/usePrivateLoadEmail'

interface LoadExpandedRowProps {
    load: CanonicalLoad
    originLocation?: LocationData | null
    onLoadUpdate?: (updatedLoad: CanonicalLoad) => void
    onEmailClick?: (load: CanonicalLoad) => void
    sendingEmail?: boolean
    errorEmail?: boolean
    isOdd?: boolean
    /** When true, render a 4th column with PrivateLoadEmailChat for the active thread.
     *  Load-hub sets this true; agentic-hub sets it false so the sidebar rail handles email. */
    showInlineEmailChat?: boolean
    emailThreadId?: string
    loadMongoId?: string
    analyticsSurface?: 'load_hub'
}

const MIN_COL_PERCENT = 10

export const LoadExpandedRow: React.FC<LoadExpandedRowProps> = ({
    load,
    originLocation,
    onLoadUpdate,
    onEmailClick,
    sendingEmail,
    errorEmail,
    isOdd = false,
    showInlineEmailChat = false,
    emailThreadId,
    loadMongoId,
    analyticsSurface,
}) => {
    const handleDetailResolved = useCallback(
        (result: Parameters<typeof trackLoadDetailsResolved>[0] extends infer T ? Omit<T & object, 'load'> : never) => {
            if (analyticsSurface === 'load_hub') {
                const { resolvedLoad, ...analyticsResult } = result as typeof result & { resolvedLoad?: CanonicalLoad }
                trackLoadDetailsResolved({ load: resolvedLoad ?? load, ...analyticsResult })
            }
        },
        [analyticsSurface, load]
    )
    const { hasDetailView, loadDetailLoading, detailStops, sylectusDetail } = useLoadDetailFetch(load, onLoadUpdate, handleDetailResolved)
    const [offerBookType, setOfferBookType] = useState<'offer' | 'book' | null>(null)
    const [emailComposeId, setEmailComposeId] = useState<string | undefined>(undefined)

    const showEmailCol = showInlineEmailChat && !!emailThreadId

    // Column widths in percent. 4-col when inline email is on (Equipment | Trip/Map | Broker | Email),
    // 3-col otherwise. Sum must remain 100. Resize handle at index i resizes [i] and [i+1].
    const [colWidths, setColWidths] = useState<number[]>(() => (showEmailCol ? [22, 28, 28, 22] : [28, 36, 36]))

    // If the email-col gate flips (load gets/loses a thread mid-session), re-seed widths so
    // the array length matches the rendered column count.
    useEffect(() => {
        setColWidths(showEmailCol ? [22, 28, 28, 22] : [28, 36, 36])
    }, [showEmailCol])

    const containerRef = useRef<HTMLDivElement>(null)
    const dragRef = useRef<{ startX: number; startWidths: number[]; handleIndex: number; containerWidth: number } | null>(null)
    const trackedDetailRef = useRef<string | null>(null)

    const handleMouseDown = useCallback(
        (handleIndex: number) => (e: React.MouseEvent) => {
            e.preventDefault()
            if (!containerRef.current) return
            dragRef.current = {
                startX: e.clientX,
                startWidths: [...colWidths],
                handleIndex,
                containerWidth: containerRef.current.getBoundingClientRect().width,
            }
            const handleMove = (ev: MouseEvent) => {
                if (!dragRef.current) return
                const { startX, startWidths, handleIndex: i, containerWidth } = dragRef.current
                const deltaPx = ev.clientX - startX
                const deltaPercent = (deltaPx / containerWidth) * 100
                const next = [...startWidths]
                const proposedLeft = startWidths[i] + deltaPercent
                const proposedRight = startWidths[i + 1] - deltaPercent
                if (proposedLeft < MIN_COL_PERCENT || proposedRight < MIN_COL_PERCENT) return
                next[i] = proposedLeft
                next[i + 1] = proposedRight
                setColWidths(next)
            }
            const handleUp = () => {
                dragRef.current = null
                document.removeEventListener('mousemove', handleMove)
                document.removeEventListener('mouseup', handleUp)
                document.body.style.cursor = ''
                document.body.style.userSelect = ''
            }
            document.addEventListener('mousemove', handleMove)
            document.addEventListener('mouseup', handleUp)
            document.body.style.cursor = 'col-resize'
            document.body.style.userSelect = 'none'
        },
        [colWidths]
    )

    useEffect(() => {
        const detailKey = `${load.sourceType}:${load.externalId ?? load.id}`
        if (analyticsSurface === 'load_hub' && trackedDetailRef.current !== detailKey) {
            trackedDetailRef.current = detailKey
            trackLoadInteraction(load.sourceType, 'details_opened')
            trackLoadDetailsOpened(load)
        }
    }, [analyticsSurface, load])

    useEffect(() => {
        window.dispatchEvent(
            new CustomEvent('numeo:loadDetailExpanded', {
                detail: { loadId: load.id?.toString(), mcNumber: load.brokerMc || null },
            })
        )
        return () => {
            // Defer collapse event so extension cleanup runs AFTER React finishes DOM reconciliation.
            // The extension's removeFactoringWidget() calls root.unmount() + container.remove()
            // which corrupts React's DOM tree if it runs during React's own unmount phase.
            const loadId = load.id?.toString()
            setTimeout(() => {
                window.dispatchEvent(
                    new CustomEvent('numeo:loadDetailCollapsed', {
                        detail: { loadId },
                    })
                )
            }, 0)
        }
    }, [load.id, load.externalId, load.brokerMc])

    // Reference uses same zebra bg as row — zebra even/odd mapped to --lh-row-zebra-a / --lh-row-zebra-b.
    const bgClass = isOdd ? 'bg-[var(--lh-row-zebra-b)]' : 'bg-[var(--lh-row-zebra-a)]'

    // Build the 3 column nodes:
    //   Col 1: Equipment Details (no card, transparent on zebra bg)
    //   Col 2: Trip/Map (no card, segmented tab at top)
    //   Col 3: Broker Info (Nolan Transportation) — WHITE card (keeps surface bg), factoring badge lives here
    //
    // For Convoy loads we lift the OfferPage fetch up here so col 1 (ConvoyDetailSection)
    // and col 2 (TripScheduleSection — Convoy stops) share a single network call. The
    // hook is no-op for non-Convoy loads.
    const { detail: convoyDetail, loading: convoyLoading, error: convoyError } = useConvoyOfferDetail(load)

    const colEquipment =
        load.sourceType === 'CONVOY' ? (
            <ConvoyDetailSection detail={convoyDetail} loading={convoyLoading} error={convoyError} />
        ) : (
            <EquipmentDetailsSection load={load} />
        )
    const colTrip = (
        <TripScheduleSection
            load={load}
            detailStops={detailStops}
            loadDetailLoading={loadDetailLoading}
            originLocation={originLocation}
            onLoadUpdate={onLoadUpdate}
            convoyDetail={convoyDetail}
            onDetailToolOpened={(tool) => {
                if (analyticsSurface === 'load_hub') trackDetailToolOpened(load, tool)
            }}
        />
    )
    // Broker column — when OfferBook compose is active, show compose instead of broker info
    const colBroker = offerBookType ? (
        <OfferBookEmailCompose
            key={offerBookType}
            load={load}
            type={offerBookType}
            onClose={() => setOfferBookType(null)}
            analyticsSurface={analyticsSurface}
            analyticsComposeId={emailComposeId}
        />
    ) : (
        <InformationSection
            load={load}
            hasDetailView={hasDetailView}
            loadDetailLoading={loadDetailLoading}
            onLoadUpdate={onLoadUpdate}
            onEmailClick={onEmailClick}
            sendingEmail={sendingEmail}
            errorEmail={errorEmail}
            onOpenOfferBook={(type) => {
                if (analyticsSurface === 'load_hub') {
                    trackLoadInteraction(load.sourceType, type === 'offer' ? 'offer_compose_opened' : 'book_compose_opened')
                    setEmailComposeId(
                        trackEmailComposerOpened({
                            load,
                            emailType: toDetailedEmailType(type),
                            emailConnected: Boolean(getCurrentEmailConfig()?.selectedEmail),
                            entryPoint: 'load_details',
                        })
                    )
                }
                setOfferBookType(type)
            }}
            onInteraction={(action, value) => {
                if (analyticsSurface !== 'load_hub') return
                if (action === 'call_clicked') {
                    trackLoadInteraction(load.sourceType, action)
                    const callProvider = value === 'googlevoice' || value === 'ringcentral' || value === 'call' ? value : 'unknown'
                    const contactAttemptId = trackLoadContactClicked(load, 'phone', 'load_details', callProvider)
                    trackCallHandoffResolved({
                        load,
                        contactAttemptId,
                        callProvider,
                        outcome: callProvider === 'call' ? 'unobservable' : 'launched',
                    })
                } else if (action === 'external_loadboard_opened') {
                    trackLoadInteraction(load.sourceType, action)
                }
            }}
            onContactCopied={(field) => {
                if (analyticsSurface === 'load_hub') trackLoadContactCopied(load, field, 'load_details')
            }}
            sylectusDetail={sylectusDetail}
            convoyDetail={convoyDetail}
        />
    )
    return (
        <div
            id={`load_detail_${load.id?.toString()}`}
            className={cn('border-b border-[var(--lh-border)] relative z-0 animate-[lhFadeIn_0.2s_ease-out]', bgClass)}
        >
            {/* Outer pr-4 gives breathing room between the last column and the page edge.
                Height clamped to a tight max so the panel stays compact; columns scroll
                internally when content exceeds. */}
            <div ref={containerRef} className={cn('flex items-stretch overflow-hidden min-w-[800px] pr-4', showEmailCol ? 'h-[380px]' : 'max-h-[280px]')}>
                {/* Col 1 — Equipment (transparent, no card) */}
                <div style={{ width: `${colWidths[0]}%` }} className="flex-shrink-0 overflow-hidden px-2 py-1.5 flex flex-col">
                    {colEquipment}
                </div>
                <ResizeHandle onMouseDown={handleMouseDown(0)} />
                {/* Col 2 — Trip/Map (transparent, no card) */}
                <div style={{ width: `${colWidths[1]}%` }} className="flex-shrink-0 overflow-hidden px-2 py-1.5 flex flex-col">
                    {colTrip}
                </div>
                <ResizeHandle onMouseDown={handleMouseDown(1)} />
                {/* Col 3 — Broker Info (also transparent, no card — reference parity) */}
                <div style={{ width: `${colWidths[2]}%` }} className="flex-shrink-0 overflow-hidden px-2 py-1.5 flex flex-col">
                    {colBroker}
                </div>
                {showEmailCol && emailThreadId && (
                    <>
                        <ResizeHandle onMouseDown={handleMouseDown(2)} />
                        {/* Col 4 — Inline email chat (load-hub only; agentic-hub uses the sidebar rail) */}
                        <div style={{ width: `${colWidths[3]}%` }} className="flex-shrink-0 overflow-hidden px-2 py-1.5 flex flex-col">
                            <PrivateLoadEmailChat load={load} emailThreadId={emailThreadId} loadMongoId={loadMongoId} isExpanded={true} />
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
